/**
 * Accepted review items → library writes. Formulas-only → one formula set;
 * rules-only → one rule set; package → both sets + the package linking them
 * (sequential — the package needs both ids). Per-item results, never
 * all-or-nothing; every write invalidates the library.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { FlowOptimizerLibraryPromptTarget } from '@trafficpulse/shared';

import { replaceFormulaFromPayload } from '@/features/flow-optimizer/clipboard/formula-clipboard.apply';

import { libraryKeys } from '../api/keys';
import { createSet } from '../api/client/sets-api';
import { createPackage } from '../api/client/packages-api';
import { applyRuleSetPayload } from '../envelope/apply-rule-set';
import { assembleAccepted } from '../validate/assemble-accepted';
import type { AssembledLibraryPayload } from '../validate/assemble-accepted';
import type { ReviewItem } from '../validate/review-items';

export interface CreateAcceptedInput {
  items: ReviewItem[];
  name: string;
  target: FlowOptimizerLibraryPromptTarget;
}

export interface CreateResultRow {
  label: string;
  ok: boolean;
  detail?: string;
}

export interface CreateAcceptedResult {
  rows: CreateResultRow[];
  assembled: AssembledLibraryPayload;
}

function reason(error: unknown): string {
  return error instanceof Error ? error.message : 'Save failed';
}

async function createFormulaSet(
  name: string,
  assembled: AssembledLibraryPayload,
): Promise<{ id?: string; row: CreateResultRow }> {
  if (!assembled.formula) {
    return { row: { label: name, ok: false, detail: 'No accepted formula groups.' } };
  }
  try {
    const set = await createSet('formula', {
      name,
      formula: replaceFormulaFromPayload(assembled.formula),
    });
    return { id: set.id, row: { label: `Formula set "${set.name}"`, ok: true } };
  } catch (error) {
    return { row: { label: `Formula set "${name}"`, ok: false, detail: reason(error) } };
  }
}

async function createRuleSet(
  name: string,
  assembled: AssembledLibraryPayload,
): Promise<{ id?: string; row: CreateResultRow }> {
  if (!assembled.rules) {
    return { row: { label: name, ok: false, detail: 'No accepted rules.' } };
  }
  try {
    const applied = applyRuleSetPayload(assembled.rules);
    const set = await createSet('rule', { name, ...applied });
    return { id: set.id, row: { label: `Rule set "${set.name}"`, ok: true } };
  } catch (error) {
    return { row: { label: `Rule set "${name}"`, ok: false, detail: reason(error) } };
  }
}

async function runCreate(
  input: CreateAcceptedInput,
): Promise<CreateAcceptedResult> {
  const assembled = assembleAccepted(input.items);
  const rows: CreateResultRow[] = [];
  if (input.target === 'formulas') {
    rows.push((await createFormulaSet(input.name, assembled)).row);
    return { rows, assembled };
  }
  if (input.target === 'rules') {
    rows.push((await createRuleSet(input.name, assembled)).row);
    return { rows, assembled };
  }
  const formula = await createFormulaSet(`${input.name} — formulas`, assembled);
  const rules = await createRuleSet(`${input.name} — rules`, assembled);
  rows.push(formula.row, rules.row);
  if (formula.id && rules.id) {
    try {
      const pkg = await createPackage({
        name: input.name,
        formulaSetId: formula.id,
        ruleSetId: rules.id,
      });
      rows.push({ label: `Package "${pkg.name}"`, ok: true });
    } catch (error) {
      rows.push({ label: `Package "${input.name}"`, ok: false, detail: reason(error) });
    }
  } else {
    rows.push({
      label: `Package "${input.name}"`,
      ok: false,
      detail: 'Skipped — both sets must save first.',
    });
  }
  return { rows, assembled };
}

export function useCreateAccepted() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: runCreate,
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: libraryKeys.all }),
  });
}
